import { Box, Tooltip } from '@material-ui/core'
import {
  DEFAULT_BOOKING_WINDOW_DAYS,
  MAX_BOOKING_WINDOW_DAYS,
  MIN_BOOKING_WINDOW_DAYS,
} from '@shared/core/appointment-types'
import { Tag } from 'lucide-react'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useCalendars } from 'src/modules/calendar/hooks/use-calendars'
import {
  Button,
  FormControl,
  Icon,
  Loader,
  SelectField,
  TextField,
  ToggleSwitch,
  Typography,
} from 'src/modules/core/components'
import { useMessageTemplates } from 'src/modules/message-templates/hooks/use-message-templates'
import { useCreateAppointmentType } from 'src/modules/user/hooks/use-appointment-types'
import { useProfile } from 'src/modules/user/hooks/use-profile'
import {
  BOOKING_WINDOW_HELPER_TEXT,
  BOOKING_WINDOW_LABEL,
  bookingWindowModeOptions,
  formDataToBookingWindow,
  formDataToMinutes,
  shouldWarnWindowShorterThanNotice,
  validateMinuteCap,
  type BookingWindowMode,
} from 'src/modules/user/utils/appointment-type-form'
import classes from './booking-appointment-type-onboarding.module.scss'

interface AppointmentTypeFormData {
  name: string
  calendarId: string
  duration: number
  minimumNotice: number
  minimumNoticeUnit: 'minutes' | 'hours' | 'days'
  bookingWindowMode: BookingWindowMode
  bookingWindowDays: number
  bufferBefore: number
  bufferAfter: number
  disableRescheduling: boolean
}

const defaultValues: AppointmentTypeFormData = {
  name: '',
  calendarId: '',
  duration: 30,
  minimumNotice: 16,
  minimumNoticeUnit: 'hours',
  bookingWindowMode: 'days',
  bookingWindowDays: DEFAULT_BOOKING_WINDOW_DAYS,
  bufferBefore: 0,
  bufferAfter: 0,
  disableRescheduling: false,
}

const noticeUnitOptions = [
  { value: 'minutes', label: 'minutes' },
  { value: 'hours', label: 'hours' },
  { value: 'days', label: 'days' },
]

export function BookingAppointmentTypeOnboarding() {
  const { data: user } = useProfile()
  const calendars = useCalendars()
  const messageTemplates = useMessageTemplates()
  const createAppointmentType = useCreateAppointmentType()

  // Get default calendar (primary/first selected)
  const defaultCalendarId = useMemo(() => {
    if (!calendars.data) return ''
    const primaryCalendar = calendars.data.find((cal) => cal.primary)
    if (primaryCalendar) return primaryCalendar.id
    const selectedCalendar = calendars.data.find((cal) => cal.selected)
    if (selectedCalendar) return selectedCalendar.id
    return calendars.data[0]?.id || ''
  }, [calendars.data])

  // Get default message template ID (template with is_default = true) for when sendReminders is true
  const defaultMessageTemplateId = useMemo(() => {
    if (!messageTemplates.data) return null
    const defaultTemplate = messageTemplates.data.find((template) => template.is_default)
    return defaultTemplate?.id || null
  }, [messageTemplates.data])

  const formDefaultValues = useMemo(() => {
    return {
      ...defaultValues,
      calendarId: defaultCalendarId,
    }
  }, [defaultCalendarId])

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
    watch,
  } = useForm<AppointmentTypeFormData>({
    defaultValues: formDefaultValues,
  })

  const bookingWindowMode = watch('bookingWindowMode')
  const showBookingWindowWarning = shouldWarnWindowShorterThanNotice(
    formDataToBookingWindow(bookingWindowMode, Number(watch('bookingWindowDays'))),
    formDataToMinutes(Number(watch('minimumNotice')), watch('minimumNoticeUnit')),
  )

  // Reset form when default values change
  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const onSubmit = async (data: AppointmentTypeFormData) => {
    if (!user?.id || !data.calendarId) {
      console.error('Missing user ID or calendar ID')
      return
    }

    const minimumNoticeMinutes = formDataToMinutes(data.minimumNotice, data.minimumNoticeUnit)

    try {
      await createAppointmentType.mutateAsync({
        calendar_id: data.calendarId,
        name: data.name,
        duration_minutes: data.duration,
        buffer_before_minutes: data.bufferBefore,
        buffer_after_minutes: data.bufferAfter,
        minimum_notice_minutes: minimumNoticeMinutes,
        booking_window_days: formDataToBookingWindow(data.bookingWindowMode, Number(data.bookingWindowDays)),
        set_reminders_using_template_id: defaultMessageTemplateId,
        allow_customer_reschedule: !data.disableRescheduling,
      })
      // The wrapper will automatically advance to the next step when bookingOnboarding updates
    } catch (error) {
      console.error('Failed to create appointment type:', error)
    }
  }

  const isLoading = createAppointmentType.isLoading || calendars.isLoading

  if (calendars.isLoading) {
    return (
      <div className={classes.onboardingSection}>
        <Loader isLoading={calendars.isLoading} />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className={classes.onboardingSection}>
      <Typography variant="h5" className={classes.title}>
        <Tag />
        Create Your First Appointment Type
      </Typography>
      <Typography variant="body" className={classes.subtitle}>
        Define the types of appointments customers can book with you
      </Typography>

      {/* Name */}
      <FormControl label="Name" error={errors.name?.message} className={classes.fieldWrapper}>
        <Controller
          name="name"
          control={control}
          rules={{ required: 'Name is required' }}
          render={({ field }) => (
            <TextField
              type="text"
              value={field.value}
              onChange={field.onChange}
              placeholder="e.g., Consultation, Follow-up"
              className={classes.nameInput}
            />
          )}
        />
      </FormControl>

      {/* Line 2: Duration and Minimum Notice */}
      <div className={classes.row}>
        <FormControl label="Duration (minutes)" error={errors.duration?.message} className={classes.fieldWrapper}>
          <Controller
            name="duration"
            control={control}
            rules={{
              required: 'Duration is required',
              min: { value: 1, message: 'Duration must be at least 1 minute' },
              validate: (value) => validateMinuteCap('duration_minutes', Number(value)),
            }}
            render={({ field }) => (
              <TextField
                type="number"
                value={field.value}
                onChange={(e) => field.onChange(Number(e.target.value))}
                className={classes.numberInput}
              />
            )}
          />
        </FormControl>

        <div className={classes.minimumNoticeWrapper}>
          <FormControl label="Minimum Notice" error={errors.minimumNotice?.message} className={classes.fieldWrapper}>
            <div className={classes.minimumNoticeInputs}>
              <Controller
                name="minimumNotice"
                control={control}
                rules={{
                  required: 'Minimum notice is required',
                  min: { value: 0, message: 'Minimum notice cannot be negative' },
                  validate: (value) =>
                    validateMinuteCap(
                      'minimum_notice_minutes',
                      formDataToMinutes(Number(value), getValues('minimumNoticeUnit')),
                    ),
                }}
                render={({ field }) => (
                  <TextField
                    type="number"
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    className={classes.numberInput}
                  />
                )}
              />
              <Controller
                name="minimumNoticeUnit"
                control={control}
                render={({ field }) => (
                  <SelectField
                    value={noticeUnitOptions.find((option) => option.value === field.value) || null}
                    onChange={(option: { value: 'minutes' | 'hours' | 'days'; label: string } | null) =>
                      field.onChange(option?.value || 'minutes')
                    }
                    options={noticeUnitOptions}
                    className={classes.unitSelect}
                  />
                )}
              />
            </div>
          </FormControl>
        </div>
      </div>

      {/* Booking Window: same control and default as the settings modal */}
      <div className={classes.row}>
        <FormControl
          label={BOOKING_WINDOW_LABEL}
          error={errors.bookingWindowDays?.message}
          className={classes.fieldWrapper}
        >
          <div className={classes.minimumNoticeInputs}>
            <Controller
              name="bookingWindowMode"
              control={control}
              render={({ field }) => (
                <SelectField
                  value={bookingWindowModeOptions.find((option) => option.value === field.value) || null}
                  onChange={(option: { value: BookingWindowMode; label: string } | null) =>
                    field.onChange(option?.value || 'indefinite')
                  }
                  options={bookingWindowModeOptions}
                />
              )}
            />
            {bookingWindowMode === 'days' && (
              <Controller
                name="bookingWindowDays"
                control={control}
                rules={{
                  validate: (value) => {
                    if (bookingWindowMode !== 'days') return true
                    const days = Number(value)
                    if (!Number.isInteger(days)) return 'Enter a whole number of days'
                    if (days < MIN_BOOKING_WINDOW_DAYS || days > MAX_BOOKING_WINDOW_DAYS) {
                      return `Enter a number between ${MIN_BOOKING_WINDOW_DAYS} and ${MAX_BOOKING_WINDOW_DAYS}`
                    }
                    return true
                  },
                }}
                render={({ field }) => (
                  <TextField
                    type="number"
                    value={field.value}
                    onChange={(e) => field.onChange(Number(e.target.value))}
                    className={classes.numberInput}
                  />
                )}
              />
            )}
          </div>
          <Typography variant="body" className={classes.rescheduleSubtext}>
            {BOOKING_WINDOW_HELPER_TEXT}
          </Typography>
          {showBookingWindowWarning && (
            <Typography variant="body" className={classes.rescheduleSubtext} role="alert">
              This window closes before your minimum notice elapses, so nothing would be bookable. You can still save.
            </Typography>
          )}
        </FormControl>
      </div>

      {/* Line 3: Buffer Before and Buffer After */}
      <div className={classes.row}>
        <FormControl
          label="Buffer Before (minutes)"
          error={errors.bufferBefore?.message}
          className={classes.fieldWrapper}
        >
          <Controller
            name="bufferBefore"
            control={control}
            rules={{
              required: 'Buffer before is required',
              min: { value: 0, message: 'Buffer before cannot be negative' },
              validate: (value) => validateMinuteCap('buffer_before_minutes', Number(value)),
            }}
            render={({ field }) => (
              <TextField
                type="number"
                value={field.value}
                onChange={(e) => field.onChange(Number(e.target.value))}
                className={classes.numberInput}
              />
            )}
          />
        </FormControl>

        <FormControl
          label="Buffer After (minutes)"
          error={errors.bufferAfter?.message}
          className={classes.fieldWrapper}
        >
          <Controller
            name="bufferAfter"
            control={control}
            rules={{
              required: 'Buffer after is required',
              min: { value: 0, message: 'Buffer after cannot be negative' },
              validate: (value) => validateMinuteCap('buffer_after_minutes', Number(value)),
            }}
            render={({ field }) => (
              <TextField
                type="number"
                value={field.value}
                onChange={(e) => field.onChange(Number(e.target.value))}
                className={classes.numberInput}
              />
            )}
          />
        </FormControl>
      </div>

      {/* Disable rescheduling */}
      <div className={classes.rescheduleField}>
        <div className={classes.rescheduleLabelBlock}>
          <div className={classes.rescheduleLabelRow}>
            <Typography variant="body">Disable rescheduling</Typography>
            <Tooltip
              title={
                <Typography variant="body" className={classes.tooltipText}>
                  By default, customers can reschedule up to the Minimum Notice period before the appointment. Enable
                  this to block rescheduling for this appointment type only.
                </Typography>
              }
              placement="top"
              arrow
            >
              <Box component="span" className={classes.tooltipIconContainer}>
                <Icon name="questionMark" />
              </Box>
            </Tooltip>
          </div>
          <Typography variant="body" className={classes.rescheduleSubtext}>
            For this Appointment Type, only
          </Typography>
        </div>
        <Controller
          name="disableRescheduling"
          control={control}
          render={({ field }) => <ToggleSwitch checked={field.value} onChange={field.onChange} />}
        />
      </div>

      <hr className={classes.separator} />

      {/* Calendar Selection */}
      <FormControl label="Linked Calendar" error={errors.calendarId?.message} className={classes.fieldWrapper}>
        <Controller
          name="calendarId"
          control={control}
          rules={{ required: 'Calendar is required' }}
          render={({ field }) => {
            const calendarName =
              calendars.data?.find((cal) => cal.id === field.value)?.title ||
              calendars.data?.find((cal) => cal.id === field.value)?.calendar_id ||
              ''
            return <TextField type="text" value={calendarName} readOnly className={classes.calendarInput} />
          }}
        />
        <Typography variant="body" className={classes.calendarHelperText}>
          We&apos;ll automatically add appointments to this calendar when customers book with you.
        </Typography>
      </FormControl>

      <div className={classes.formActions}>
        <Button type="submit" variant="primary" disabled={isLoading} className={classes.continueButton}>
          {isLoading ? 'Creating...' : 'Continue'}
        </Button>
      </div>
    </form>
  )
}
